import { QuestionCircleIcon, ArrowChevronRightMiniIcon } from '@calendly/ui/components/icons'
import { Link } from '@calendly/ui/components/link'
import freeEmailDomains from 'free-email-domains'

import { SETTINGS_SECTION } from 'features/settings_modal/constants'
import { SETTINGS_MODAL_AI_TAB } from 'features/settings_modal/settings_modal_ai_page'
import {
  getSettingsNavigationLinkProps,
  useSettingsNavigation,
} from 'features/settings_modal/use_settings_navigation'
import { NOTETAKER_SETTINGS_PATH } from 'shared/constants/personal_browser_urls'

import { VALID_MI_CALENDARS } from './constants'

export const TermsLink = ({ children }) => (
  <Link
    href="https://calendly.com/legal/customer-terms-conditions"
    target="_blank"
    rel="noopener noreferrer"
    underline={false}
    variant="subtle"
  >
    {children}
  </Link>
)

export const LearnMoreLinkMeetingRecaps = ({ children }) => (
  <Link
    href="https://calendly.com/help/notetaker-overview"
    target="_blank"
    rel="noopener noreferrer"
    underline={false}
  >
    <QuestionCircleIcon />
    {children}
    <ArrowChevronRightMiniIcon />
  </Link>
)

export const UserSettingsLink = ({ children }) => {
  const { hrefForSettingsModalEntry, navigateToSettingsModalEntry } = useSettingsNavigation()
  const settingsTarget = hrefForSettingsModalEntry(
    SETTINGS_SECTION.ai_notetaker,
    NOTETAKER_SETTINGS_PATH,
    SETTINGS_MODAL_AI_TAB.NOTETAKER,
  )

  if (typeof settingsTarget === 'string') {
    return (
      <Link
        href={settingsTarget}
        target="_blank"
        rel="noopener noreferrer"
        underline={false}
      >
        {children}
      </Link>
    )
  }

  const settingsLinkProps = getSettingsNavigationLinkProps({
    target: settingsTarget,
    navigate: () => navigateToSettingsModalEntry(SETTINGS_SECTION.ai_notetaker, {
      legacyPath: NOTETAKER_SETTINGS_PATH,
      settingsTab: SETTINGS_MODAL_AI_TAB.NOTETAKER,
    }),
  })

  return (
    <Link {...settingsLinkProps} underline={false}>
      {children}
    </Link>
  )
}

export const handleCheckCalInvalid = (kind = '', isNotetakerOutlookSettingsEnabled = false) => {
  const validCalendars = [...VALID_MI_CALENDARS]
  if (isNotetakerOutlookSettingsEnabled) {
    validCalendars.push('outlook')
  }
  return validCalendars.includes(kind.toLowerCase())
}

export const handleDefaults = (e) => {
  e.stopPropagation()
  e.preventDefault()
}

export const handleCheckStepEnabled = (location, param) => new URLSearchParams(location.search).get(param) === 'true'

export const getPushCalendarData = (data) => {
  if (!Array.isArray(data) || !data.length) return false

  return data.find((cal) => cal?.email) || false
}

export const checkIsFreeEmailDomain = (email) => {
  if (typeof email !== 'string') return false
  const domain = email.split('@')[1]
  return domain ? freeEmailDomains.includes(domain) : false
}

// Onboarding must not send this key from show_default_user_settings so the backend can
// apply GDPR-conditional default-on
export const omitPreMeetingRecordingNoticeFromDefaults = (defaultUserSettings) => {
  if (!defaultUserSettings) return {}

  const settings = { ...defaultUserSettings }
  delete settings.preMeetingRecordingNotice
  delete settings.pre_meeting_recording_notice
  return settings
}

const LOCATION_KIND_DISPLAY_NAMES = {
  ZOOM: 'Zoom',
  GOOGLE_MEET: 'Google Meet',
  MICROSOFT_TEAMS: 'Microsoft Teams',
  zoom_conference: 'Zoom',
  google_conference: 'Google Meet',
  microsoft_teams_conference: 'Microsoft Teams',
  zoom: 'Zoom',
  google_conferencing: 'Google Meet'
}

export const getLocationKindDisplayName = (event) => (
  LOCATION_KIND_DISPLAY_NAMES[event?.locationKind]
  ?? LOCATION_KIND_DISPLAY_NAMES[event?.locationType]
  ?? LOCATION_KIND_DISPLAY_NAMES[event?.location_kind]
  ?? null
)
